import React from 'react';
import { User, DollarSign, MessageSquare, MoreHorizontal, Star, Building2, Clock } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@vaiu/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@vaiu/ui/dropdown-menu';
import { Contact } from '@/services/platformApi';
import { CurrencyDisplay } from '@vaiu/ui';
import { currencyService } from '@/services/currencyService';
import type { MerchantProfile } from '@/types/api/merchant';

interface ContactCardProps {
  contact: Contact;
  onClick: () => void;
  onSendMoney: () => void;
  isLast?: boolean;
  merchantProfile?: any; // MerchantProfile from cache
  totalsByCurrency?: Record<string, { sent: number; received: number }>;
  userCurrency?: string;
}

const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  onClick,
  onSendMoney,
  isLast = false,
  merchantProfile: cachedMerchantProfile,
  totalsByCurrency,
  userCurrency = 'USD'
}) => {
  // Use cached merchant profile only - no individual fetching to avoid rate limits
  const merchantProfile = cachedMerchantProfile || null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Determine avatar source: merchant logo for business contacts, or contact avatar, or placeholder
  const getAvatarSource = () => {
    if (contact.relationship === 'business' && merchantProfile?.logo) {
      return merchantProfile.logo;
    }
    if (contact.avatar) {
      return contact.avatar;
    }
    return undefined; // Will show fallback with initials
  };

  // Determine display name: merchant business name for business contacts, or contact name
  const getDisplayName = () => {
    if (contact.relationship === 'business' && merchantProfile?.businessName) {
      return merchantProfile.businessName;
    }
    return contact.name;
  };

  const getRelationshipBadge = () => {
    const badgeMap = {
      frequent: { text: 'Frequent', variant: 'default' as const, icon: Star },
      recent: { text: 'Recent', variant: 'secondary' as const, icon: Clock },
      business: { text: 'Business', variant: 'outline' as const, icon: Building2 },
      infrequent: { text: 'Contact', variant: 'secondary' as const, icon: User },
    };
    
    const config = badgeMap[contact.relationship] || badgeMap.infrequent;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center space-x-1 flex-shrink-0 whitespace-nowrap">
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span>{config.text}</span>
      </Badge>
    );
  };

  const formatLastTransaction = () => {
    if (!contact.lastTransactionDate) return 'No tokens';
    
    const date = new Date(contact.lastTransactionDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else if (diffDays <= 30) {
      return `${Math.ceil(diffDays / 7)} weeks ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getTransactionSummary = () => {
    if (contact.totalTransactions === 0) {
      return <span>No tokens</span>;
    }
    
    // If we have currency-grouped totals, display them
    if (totalsByCurrency && Object.keys(totalsByCurrency).length > 0) {
      const currencyEntries = Object.entries(totalsByCurrency);
      
      // Sort: user's currency first, then others alphabetically
      const sortedCurrencies = currencyEntries.sort(([currA], [currB]) => {
        if (currA === userCurrency) return -1;
        if (currB === userCurrency) return 1;
        return currA.localeCompare(currB);
      });
      
      // Build display parts for each currency
      const currencyParts = sortedCurrencies.map(([currency, totals]) => {
        const totalAmount = totals.sent + totals.received;
        if (totalAmount === 0) return null;
        
        return (
          <span key={currency} className="inline-flex items-center gap-1">
            <span className="font-medium">{currency}:</span>
            <CurrencyDisplay amount={totalAmount} currency={currency} currencyService={currencyService} size="sm" />
          </span>
        );
      }).filter(Boolean);
      
      if (currencyParts.length > 0) {
        return (
          <span className="flex items-center gap-2 flex-wrap">
            {currencyParts.map((part, index) => (
              <React.Fragment key={index}>
                {part}
                {index < currencyParts.length - 1 && <span className="text-muted-foreground">•</span>}
              </React.Fragment>
            ))}
          </span>
        );
      }
    }
    
    // Fallback: use contact totals (no currency info, assume user's currency)
    const parts = [];
    if (contact.totalSent > 0) {
      parts.push(
        <span key="sent" className="inline-flex items-center gap-1">
          <span>Sent:</span>
          <CurrencyDisplay amount={contact.totalSent} currency={userCurrency} currencyService={currencyService} size="sm" />
        </span>
      );
    }
    if (contact.totalReceived > 0) {
      parts.push(
        <span key="received" className="inline-flex items-center gap-1">
          <span>Received:</span>
          <CurrencyDisplay amount={contact.totalReceived} currency={userCurrency} currencyService={currencyService} size="sm" />
        </span>
      );
    }
    
    if (parts.length > 0) {
      return (
        <span className="flex items-center gap-2 flex-wrap">
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {index < parts.length - 1 && <span className="text-muted-foreground">•</span>}
            </React.Fragment>
          ))}
        </span>
      );
    }
    
    // Final fallback: just show token count
    return (
      <span>
        {contact.totalTransactions} {contact.totalTransactions === 1 ? 'token' : 'tokens'}
      </span>
    );
  };

  return (
    <div
      className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
        isLast ? '' : 'border-b'
      }`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-12 w-12 flex-shrink-0" onClick={onClick}>
          <AvatarImage src={getAvatarSource()} alt={getDisplayName()} />
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {getInitials(getDisplayName())}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0 overflow-hidden" onClick={onClick}>
          {/* Name and Badge Row */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium truncate flex-1 min-w-0">{getDisplayName()}</h3>
            <div className="flex items-center gap-1 flex-shrink-0">
              {getRelationshipBadge()}
              {contact.hasConversations && (
                <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
            </div>
          </div>
          
          {/* Email/Phone Row */}
          <p className="text-sm text-muted-foreground truncate mb-1">
            {contact.email || contact.phoneNumber || 'No contact info'}
          </p>
          
          {/* Tokens and Last Transaction Row */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="truncate min-w-0 flex-1 flex items-center gap-1 flex-wrap">
              {getTransactionSummary()}
            </div>
            <span className="flex-shrink-0 whitespace-nowrap">
              <span className="hidden sm:inline">Last: </span>
              {formatLastTransaction()}
            </span>
          </div>
        </div>
        
        {/* Menu Button - Always visible, never shrinks */}
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 flex-shrink-0"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onSendMoney}>
                <DollarSign className="w-4 h-4 mr-2" />
                Pay
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onClick}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Chat
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default ContactCard; 